import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { Drawer, FormControlLabel, MenuItem, Slider, Switch, TextField } from '@material-ui/core';
import {
  IconHomeCheck,
  IconInfoCircle,
  IconPlus,
  IconShieldCheck,
  IconUserCheck,
} from '@tabler/icons';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

import { useAuth, useResponsive } from '#root/hooks';
import { shippingService, userService } from '#root/services';
import { USER_ADDRESS } from '#root/constants/navigation';
import { DocumentType, DocumentTypeValue } from '#root/models/Shipping';
import type { DealPreconfirm, PreconfirmSummary, ValoremRange } from '#root/models/Shipping';
import {
  AddressDTO,
  addressLocationLine,
  addressReceiverLine,
  hasDocument,
} from '#root/models/Address';
import {
  clampValorem,
  defaultValorem,
  formatCurrency,
  insuranceCost,
  isValidDocNumber,
  parseYmd,
  valoremFromIndex,
  valoremSteps,
  valoremToIndex,
} from '#root/utils';
import { Button, Skeleton } from '#root/components/ui/primitives';
import AddressDocumentFields from '../AddressDocumentFields';
import type { AddressDocumentValue } from '../AddressDocumentFields';
import DrawerAppbar from '../DrawerAppbar';
import styles from './styles.module.scss';

export interface ShippingConfigDeal {
  ida: number;
  title?: string | null;
  preconfirm: DealPreconfirm;
  // Fecha de entrega que fijó el viajero ("fecha estimada")
  fecent?: string | null;
}

interface ShippingConfigDrawerProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  createMessage: {
    success: (msg: string) => void;
    error: (msg: string) => void;
  };
  deal: ShippingConfigDeal;
}

type ShippingContext = PreconfirmSummary['context'];

/** Seguro inicial (D6): el guardado acotado al rango; guardado en 0 = interruptor apagado; sin guardar = el máximo. */
const initialInsurance = (stored: number | null, range: ValoremRange) => {
  if (!range.enabled) return { insured: false, amount: 0 };
  if (stored === null || stored === undefined) return { insured: true, amount: defaultValorem(range) };
  if (stored <= 0) return { insured: false, amount: defaultValorem(range) };
  return { insured: true, amount: clampValorem(stored, range.min, range.max) };
};

/** Dirección inicial: la configurada (si sigue vigente y está en la lista); si no, la principal. */
const initialAddressId = (list: AddressDTO[], preconfirm: DealPreconfirm): number | '' => {
  const configured = preconfirm.district
    ? list.find((item) => item.id === preconfirm.address_id)
    : undefined;
  const initial = configured || list.find((item) => !!item.is_primary_address);
  return initial ? initial.id : '';
};

/** Fecha estimada de entrega ("14 de octubre"), solo si todavía no pasó. */
const upcomingDeliveryDate = (fecent?: string | null): string => {
  const date = parseYmd(fecent);
  if (!date) return '';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date >= today ? format(date, "d 'de' MMMM", { locale: es }) : '';
};

/** Drawer "Configurar envío" de un acuerdo en tránsito (§7.4): dirección y seguro de su envío nacional. */
const ShippingConfigDrawer = (props: ShippingConfigDrawerProps) => {
  const { open, onClose, onSuccess, createMessage, deal } = props;
  const { preconfirm } = deal;
  const { isDesktop } = useResponsive();
  const { usuario } = useAuth();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [addresses, setAddresses] = useState<AddressDTO[]>([]);
  const [context, setContext] = useState<ShippingContext>(undefined);
  const [selectedAddressId, setSelectedAddressId] = useState<number | ''>('');
  const [docType, setDocType] = useState<DocumentTypeValue>(DocumentType.dni);
  const [docNumber, setDocNumber] = useState('');
  const [insured, setInsured] = useState(true);
  const [amount, setAmount] = useState(0);
  // Descarta la respuesta de una apertura anterior
  const requestRef = useRef(0);

  // --- Seguro (rango del acuerdo) ---
  const range = preconfirm.valorem_range;
  const isRequired = !!range.required;
  const isInsured = range.enabled && (isRequired || insured);
  const valorem = isInsured ? amount : 0;
  // Slider por índice (0..N) para llegar exacto al máximo; N = 0 (min = max) → monto fijo
  const sliderSteps = valoremSteps(range.min, range.max);
  // Para un solo acuerdo es idéntico a estimateCost con las tarifas y no depende del GET (D-4)
  const cost = insuranceCost(valorem, range.insurance_rate, range.insurance_igv);

  // --- Dirección ---
  const hasAddresses = addresses.length > 0;
  const selectedAddress = addresses.find((item) => item.id === selectedAddressId);
  // Dirección sin documento de quien recibe: se completa aquí y viaja al guardar
  const needsDocument = !!selectedAddress && !hasDocument(selectedAddress);
  const accountName = usuario ? `${usuario.nom} ${usuario.ape}` : '';
  const receiverLine = addressReceiverLine(selectedAddress, accountName);
  // Valor elegido del selector: una línea (el selector la corta con elipsis antes de la flecha)
  const selectedAddressText = selectedAddress
    ? [selectedAddress.address_1?.trim(), addressLocationLine(selectedAddress)]
        .filter(Boolean)
        .join(', ')
    : '';

  // Ya configurado con una dirección vigente (estado 2 del badge)
  const isConfigured = !!preconfirm.district;
  const showJointShipping = !!context && (context.transit_deals >= 2 || context.open_shipments >= 1);
  const deliveryDate = upcomingDeliveryDate(deal.fecent);
  const isBusy = loading || submitting;
  const isSaveDisabled =
    isBusy || !selectedAddressId || (needsDocument && !isValidDocNumber(docType, docNumber));

  const handleAddressChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedAddressId(Number(event.target.value));
    // El documento tecleado era para la dirección anterior
    setDocType(DocumentType.dni);
    setDocNumber('');
  };

  const handleDocumentChange = (value: AddressDocumentValue) => {
    setDocType(value.docType);
    setDocNumber(value.docNumber);
  };

  // El slider entrega un índice; el estado guarda el monto en soles
  const handleAmountChange = (_event: ChangeEvent<{}>, newValue: number | number[]) => {
    setAmount(valoremFromIndex(newValue as number, range.min, range.max));
  };

  const handleSave = async () => {
    if (isSaveDisabled || !selectedAddressId) return;
    setSubmitting(true);
    const res = await shippingService.preconfirm({
      delivery_address_id: selectedAddressId,
      deals: [{ ida: deal.ida, valorem }],
      ...(needsDocument ? { document_type: docType, document_number: docNumber } : {}),
    });
    setSubmitting(false);
    if (res.error) {
      createMessage.error(res.message || 'No se pudo guardar la configuración del envío.');
      return;
    }
    createMessage.success(res.message || 'Guardamos la configuración de tu envío.');
    onSuccess();
    onClose();
  };

  const handleRemove = async () => {
    if (isBusy) return;
    setSubmitting(true);
    const res = await shippingService.cancelPreconfirm([deal.ida]);
    setSubmitting(false);
    if (res.error) {
      createMessage.error(res.message || 'No se pudo quitar la configuración del envío.');
      return;
    }
    createMessage.success(res.message || 'Quitamos la configuración de tu envío.');
    onSuccess();
    onClose();
  };

  useEffect(() => {
    if (!open) return;
    const requestId = ++requestRef.current;
    const insurance = initialInsurance(preconfirm.valorem, range);
    setLoading(true);
    setAddresses([]);
    setContext(undefined);
    setSelectedAddressId('');
    setDocType(DocumentType.dni);
    setDocNumber('');
    setInsured(insurance.insured);
    setAmount(insurance.amount);
    Promise.all([userService.address(), shippingService.getPreconfirm()]).then(
      ([addressRes, summaryRes]) => {
        if (requestId !== requestRef.current) return;
        if (addressRes.error) createMessage.error(addressRes.message);
        const list: AddressDTO[] = addressRes.error ? [] : addressRes.data?.addresses_list || [];
        setAddresses(list);
        setSelectedAddressId(initialAddressId(list, preconfirm));
        // Solo se usa el contexto del aviso de envío conjunto; si falla, no hay aviso
        setContext(summaryRes.error ? undefined : summaryRes.data?.context);
        setLoading(false);
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const content = (
    <div className={styles.main}>
      <p className={styles.dealLine}>
        Acuerdo #{deal.ida}
        {deal.title && ` · ${deal.title}`}
      </p>
      <p className={styles.narrative}>
        El viajero dejará tu pedido en una oficina de Mail Boxes ETC en Lima. Desde ahí lo enviamos con Olva a la dirección que elijas.
      </p>

      {/* Dirección */}
      <section className={styles.section}>
        <h4 className={styles.sectionTitle}>
          <IconHomeCheck size={20} />
          Dirección de entrega
        </h4>
        {loading && <Skeleton height={56} />}
        {!loading && hasAddresses && (
          <>
            <TextField
              select
              fullWidth
              size="medium"
              label="Elige la dirección"
              variant="outlined"
              value={selectedAddressId}
              onChange={handleAddressChange}
              disabled={submitting}
              SelectProps={{
                renderValue: () => selectedAddressText,
                // En el menú, cada opción completa (con salto de línea) y del ancho del drawer
                MenuProps: { PaperProps: { className: styles.addressMenu } },
              }}>
              {addresses.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  <div className={styles.addressOption}>
                    <span>{item.address_1}</span>
                    <span className={styles.addressOptionHint}>{addressLocationLine(item)}</span>
                  </div>
                </MenuItem>
              ))}
            </TextField>
            {selectedAddress && (
              <div className={styles.receiver}>
                <IconUserCheck size={16} />
                <span>Recibe: {receiverLine}</span>
              </div>
            )}
            {needsDocument && (
              <div className={styles.documentFields}>
                <AddressDocumentFields
                  docType={docType}
                  docNumber={docNumber}
                  onChange={handleDocumentChange}
                  disabled={submitting}
                />
              </div>
            )}
            <div className={styles.addAddress}>
              <Button variant="link" size="sm" icon={<IconPlus size={16} />} to={USER_ADDRESS}>
                Agregar otra dirección
              </Button>
            </div>
          </>
        )}
        {!loading && !hasAddresses && (
          <>
            <p className={styles.helper}>Crea una dirección y luego selecciónala.</p>
            <Button variant="outline" fullWidth to={USER_ADDRESS}>
              Crear nueva dirección
            </Button>
          </>
        )}
        {/* Envío conjunto (D10): habla de "la misma dirección" */}
        {showJointShipping && (
          <div className={styles.infoBox}>
            <IconInfoCircle size={18} />
            <p>
              Si tus pedidos llegan a la misma oficina de Mail Boxes y van a la misma dirección,
              intentaremos consolidarlos.
            </p>
          </div>
        )}
      </section>

      {/* Seguro */}
      <section className={styles.section}>
        <h4 className={styles.sectionTitle}>
          <IconShieldCheck size={20} />
          Seguro
        </h4>
        {!range.enabled ? (
          <p className={styles.helper}>Tu pedido vale menos de S/ 100: va sin seguro.</p>
        ) : (
          <>
            {isRequired ? (
              <p className={styles.required}>Obligatorio para pedidos de más de S/ 1,000.</p>
            ) : (
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={insured}
                    onChange={(event) => setInsured(event.target.checked)}
                    disabled={submitting}
                  />
                }
                label="Asegurar"
              />
            )}
            <p className={styles.coverage}>Cubre hasta el 95 % del valor declarado.</p>
            {isInsured ? (
              <div className={styles.slider}>
                <span className={styles.sliderLabel}>Valor a asegurar</span>
                <span className={styles.amount}>{formatCurrency(amount, 0)}</span>
                {sliderSteps > 0 && (
                  <>
                    {/* scale: aria-valuemin/max/now y getAriaValueText en soles, no en índices */}
                    <Slider
                      value={valoremToIndex(amount, range.min, range.max)}
                      onChange={handleAmountChange}
                      min={0}
                      max={sliderSteps}
                      step={1}
                      scale={(index) => valoremFromIndex(index, range.min, range.max)}
                      getAriaValueText={(value) => formatCurrency(value, 0)}
                      aria-label="Valor a asegurar"
                      valueLabelDisplay="off"
                      disabled={submitting}
                    />
                    <div className={styles.range}>
                      <span>{formatCurrency(range.min, 0)}</span>
                      <span>{formatCurrency(range.max, 0)}</span>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <p className={styles.helper}>Tu pedido irá sin seguro.</p>
            )}
            {cost > 0 && (
              <p className={styles.cost}>
                Costo estimado del seguro: <strong>{formatCurrency(cost)}</strong> (inc. IGV)
              </p>
            )}
          </>
        )}
      </section>

      <div className={styles.actions}>
        <Button fullWidth loading={submitting} disabled={isSaveDisabled} onClick={handleSave}>
          Guardar
        </Button>
        {isConfigured && (
          <Button variant="link" fullWidth disabled={isBusy} onClick={handleRemove}>
            Quitar configuración
          </Button>
        )}
      </div>

      <p className={styles.footer}>
        Puedes cambiar la dirección y el seguro hasta que el viajero entregue tu pedido
        {deliveryDate && ` (fecha estimada: ${deliveryDate})`}. El costo del envío, con el seguro
        incluido, lo pagas cuando esté listo para despacho.
      </p>
    </div>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        elevation: 0,
        className: isDesktop ? styles.drawerPaperDesktop : styles.drawerPaperMobile,
      }}
      style={isDesktop ? undefined : { zIndex: 1300 }}>
      <DrawerAppbar
        isMobile={!isDesktop}
        title="Configurar envío"
        subtitle="¿A dónde enviamos tu pedido?"
        onClose={onClose}
      />
      {content}
    </Drawer>
  );
};

export default ShippingConfigDrawer;
